import type {
  AccountUpdateInput,
  Configuration,
  ConnectAwsOrganizationRequest,
  CreateAccountInput,
  Condition,
  UpdateRestoreAccountConnectivityConfigRequest,
  ListSourceAccountsRequest,
} from '@repo/api-gw-sdk';
import {
  AccountsApi,
  CreateProjectRestoreAccountApi,
  CreateProjectSourceAccountApi,
  CreateVaultAccountApi,
  GetAWSStackDetailsApi,
  ListProjectRestoreAccountsApi,
  ListProjectSourceAccountsApi,
  ListScanningAccountsApi,
  ListVaultAccountsApi,
  UpdateProjectRestoreAccountApi,
  GetProjectSourceAccountConfigurationApi,
  GetRestoreCloudAccountTagsConfigurationApi,
  UpdateRestoreCloudAccountTagsConfigurationApi,
  DiscoveryStatusApi,
  UpdateRestoreAccountNameApi,
  UpdateSourceAccountNameApi,
} from '@repo/api-gw-sdk';

import { toJsonText } from '@/components/queryBuilder/QueryLanguageConverter';
import { encodePageToken } from '@/utils/string';

import type { HttpClient } from './httpClient';

const baseUrl = '/accounts';

export const cloudAccounts = (
  httpClient: HttpClient,
  config: Configuration,
  currentProjectId: string
) => {
  const listSourceAccountsApi = new ListProjectSourceAccountsApi(config);
  const createSourceAccountApi = new CreateProjectSourceAccountApi(config);
  const listVaultAccountsApi = new ListVaultAccountsApi(config);
  const createVaultAccountApi = new CreateVaultAccountApi(config);
  const listRestoreAccountsApi = new ListProjectRestoreAccountsApi(config);
  const createRestoreAccountApi = new CreateProjectRestoreAccountApi(config);
  const updateRestoreAccountApi = new UpdateProjectRestoreAccountApi(config);

  const listScanningAccountsApi = new ListScanningAccountsApi(config);
  const accountsApi = new AccountsApi(config);
  const discoveryStatusApi = new DiscoveryStatusApi(config);

  const getAWSStackDetailsApi = new GetAWSStackDetailsApi(config);
  const getProjectSourceAccountConfigurationApi =
    new GetProjectSourceAccountConfigurationApi(config);

  const getRestoreAccountTagsApi =
    new GetRestoreCloudAccountTagsConfigurationApi(config);
  const setRestoreAccountTagsApi =
    new UpdateRestoreCloudAccountTagsConfigurationApi(config);
  const updateRestoreAccountNameApi = new UpdateRestoreAccountNameApi(config);
  const updateSourceAccountNameApi = new UpdateSourceAccountNameApi(config);

  return {
    source: {
      listV2: (
        pageIndex?: number,
        pageSize?: number,
        request?: ListSourceAccountsRequest
      ) =>
        httpClient.execute(
          (...args: Parameters<typeof accountsApi.listSourceAccounts>) =>
            accountsApi.listSourceAccounts(
              args[0],
              typeof pageIndex === 'number'
                ? encodePageToken(pageIndex)
                : undefined,
              args[2],
              args[3]
            ),
          baseUrl,
          '/source-accounts-v2',
          currentProjectId,
          `${pageIndex}`,
          pageSize,
          request || {}
        ),
      syncPermissions: (accountId: string) =>
        accountsApi.syncSourceAccountPermissions(currentProjectId, accountId),
      discoveryStatus: (accountId: string) =>
        httpClient.execute(
          discoveryStatusApi.discoveryStatus.bind(discoveryStatusApi),
          baseUrl,
          '/source-account-discovery-status',
          currentProjectId,
          accountId
        ),
      create: (account: CreateAccountInput) =>
        createSourceAccountApi.createProjectSourceAccount(
          currentProjectId,
          account
        ),
      disconnect: (id: string) =>
        accountsApi.disconnectSourceAccount(currentProjectId, id),
      reconnect: (id: string) =>
        accountsApi.reconnectSourceAccount(currentProjectId, id),
      list: (
        projectId?: string,
        pageIndex?: number,
        pageSize?: number,
        sorting?: string,
        filter?: Condition
      ) =>
        httpClient.execute(
          (
            ...args: Parameters<
              typeof listSourceAccountsApi.listProjectSourceAccounts
            >
          ) =>
            !projectId && !currentProjectId
              ? Promise.resolve(undefined)
              : listSourceAccountsApi.listProjectSourceAccounts(...args),
          baseUrl,
          '/source-accounts',
          projectId || currentProjectId,
          pageIndex,
          pageSize,
          sorting,
          filter ? toJsonText(filter) : undefined
        ),
      config: {
        get: (accountId: string) =>
          httpClient.execute(
            (
              ...args: Parameters<
                typeof getProjectSourceAccountConfigurationApi.getProjectSourceAccountConfiguration
              >
            ) =>
              accountId
                ? getProjectSourceAccountConfigurationApi.getProjectSourceAccountConfiguration(
                    ...args
                  )
                : Promise.resolve(undefined),
            baseUrl,
            '/source-account-config',
            accountId,
            currentProjectId
          ),
      },
      metrics: {
        get: (accountId: string) => {
          return httpClient.execute(
            accountsApi.getSourceAccountMetricsConfig.bind(accountsApi),
            baseUrl,
            '/restore-account-job-metrics',
            accountId,
            currentProjectId
          );
        },
        enable: (accountId: string, region: string) => {
          return accountsApi.enableSourceAccountMetricsConfig(
            accountId,
            currentProjectId,
            {
              aws: {
                region: region,
              },
            }
          );
        },
        disable: (accountId: string) => {
          return accountsApi.disableSourceAccountMetricsConfig(
            accountId,
            currentProjectId
          );
        },
      },
      name: {
        update: (accountId: string, name?: string) =>
          updateSourceAccountNameApi.updateSourceAccountName(
            accountId,
            currentProjectId,
            name
          ),
      },
    },
    vault: {
      create: (account: CreateAccountInput) =>
        createVaultAccountApi.createVaultAccount(account),
      list: () =>
        httpClient.execute(
          listVaultAccountsApi.listVaultAccounts.bind(listVaultAccountsApi),
          baseUrl,
          '/vault-accounts'
        ),
      disconnect: (accountId: string) =>
        accountsApi.disconnectCustomerVaultAccount(accountId),
      reconnect: (accountId: string) =>
        accountsApi.reconnectCustomerVaultAccount(accountId),
    },
    restore: {
      syncPermissions: (accountId: string) =>
        accountsApi.syncRestoreAccountPermissions(currentProjectId, accountId),
      create: (account: CreateAccountInput) =>
        createRestoreAccountApi.createProjectRestoreAccount(
          currentProjectId,
          account
        ),
      update: (accountId: string, account: AccountUpdateInput) =>
        updateRestoreAccountApi.updateProjectRestoreAccount(
          accountId,
          currentProjectId,
          account
        ),
      disconnect: (id: string) =>
        accountsApi.disconnectRestoreAccount(currentProjectId, id),
      reconnect: (id: string) =>
        accountsApi.reconnectRestoreAccount(currentProjectId, id),
      list: (
        pageIndex?: number,
        pageSize?: number,
        sorting?: string,
        filter?: Condition
      ) =>
        httpClient.execute(
          listRestoreAccountsApi.listProjectRestoreAccounts.bind(
            listRestoreAccountsApi
          ),
          baseUrl,
          '/restore-accounts',
          currentProjectId,
          pageIndex,
          pageSize,
          sorting,
          filter ? toJsonText(filter) : undefined
        ),
      connectivity: {
        get: (accountId: string) =>
          httpClient.execute(
            accountsApi.getRestoreAccountConnectivityConfig.bind(accountsApi),
            baseUrl,
            '/restore-account-connectivity-config',
            accountId,
            currentProjectId
          ),
        update: (
          accountId: string,
          cloudAccountConfiguration: UpdateRestoreAccountConnectivityConfigRequest
        ) =>
          accountsApi.updateRestoreAccountConnectivityConfig(
            accountId,
            currentProjectId,
            cloudAccountConfiguration
          ),
      },
      tags: {
        get: (accountId: string) =>
          httpClient.execute(
            getRestoreAccountTagsApi.getRestoreCloudAccountTagsConfiguration.bind(
              getRestoreAccountTagsApi
            ),
            baseUrl,
            '/restore-account-tags',
            accountId,
            currentProjectId
          ),
        set: (accountId: string, tags: Record<string, string>) =>
          setRestoreAccountTagsApi.updateRestoreCloudAccountTagsConfiguration(
            accountId,
            currentProjectId,
            Object.entries(tags).map(([key, value]) => ({
              key,
              value,
            }))
          ),
      },
      metrics: {
        get: (accountId: string) => {
          return httpClient.execute(
            accountsApi.getRestoreAccountMetricsConfig.bind(accountsApi),
            baseUrl,
            '/restore-account-job-metrics',
            accountId,
            currentProjectId
          );
        },
        enable: (accountId: string, region: string) => {
          return accountsApi.enableRestoreAccountMetricsConfig(
            accountId,
            currentProjectId,
            {
              aws: {
                region: region,
              },
            }
          );
        },
        disable: (accountId: string) => {
          return accountsApi.disableRestoreAccountMetricsConfig(
            accountId,
            currentProjectId
          );
        },
      },
      name: {
        update: (accountId: string, name?: string) =>
          updateRestoreAccountNameApi.updateRestoreAccountName(
            accountId,
            currentProjectId,
            name
          ),
      },
    },
    scanning: {
      create: () => {
        throw new Error('Not implemented');
      },
      list: () =>
        httpClient.execute(
          listScanningAccountsApi.listScanningAccounts.bind(
            listScanningAccountsApi
          ),
          baseUrl,
          '/scanning-accounts'
        ),
    },
    managementGroups: {
      list: () =>
        httpClient.execute(
          accountsApi.listAzureManagementGroups.bind(accountsApi),
          baseUrl,
          '/management-groups',
          currentProjectId
        ),
      activate: (managementGroupId: string) =>
        accountsApi.activateAzureManagementGroup(
          currentProjectId,
          managementGroupId
        ),
      deactivate: (managementGroupId: string) =>
        accountsApi.deactivateAzureManagementGroup(
          currentProjectId,
          managementGroupId
        ),
    },
    organization: {
      list: (projectId?: string) =>
        httpClient.execute(
          accountsApi.listAwsOrganizations.bind(accountsApi),
          baseUrl,
          '/aws-organizations',
          projectId || currentProjectId
        ),
      connect: (connectAwsOrganizationRequest: ConnectAwsOrganizationRequest) =>
        accountsApi.connectAwsOrganization(
          currentProjectId,
          connectAwsOrganizationRequest
        ),
      activate: (organizationId: string) =>
        accountsApi.activateAwsOrganization(currentProjectId, organizationId),
      deactivate: (organizationId: string) =>
        accountsApi.deactivateAwsOrganization(currentProjectId, organizationId),
    },
    eksClusters: {
      list: () =>
        httpClient.execute(
          accountsApi.listEksClusters.bind(accountsApi),
          baseUrl,
          '/eks-clusters',
          currentProjectId
        ),
    },
    stack: {
      get: (accountId: string) =>
        getAWSStackDetailsApi.getAWSStackDetails(currentProjectId, accountId),
    },
  };
};
